import { API_CONFIG, getAuthHeaders, handleApiError, ApiResponse } from './apiConfig'

// Helper pour fetch avec configuration CORS
function fetchWithCors(url: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...options,
    mode: 'cors',
    credentials: 'include',
    headers: {
      ...API_CONFIG.headers,
      ...options.headers
    }
  })
}

// Interfaces
export interface Souscription {
  id: number
  date_debut: string
  date_fin: string
  duree_mois: number
  salaire: number
  quotite_cessible_pourcent: number
  quotite_cessible_montant: number
  pouvoir_achat: number
  pouvoir_achat_an: number
  revolving: number
  prelevement_pourcent: number
  prelevement_montant: number
  utilisation_total: number
  salaire_net: number
  actif: boolean
  details: SouscriptionDetail[]
}

export interface SouscriptionDetail {
  id: number
  mois: number
  date_prevision: string
  date_operation: string | null
  decaissement: number
  fond_risque: number
  commission: number
  revolving: number
  recharge: number
  cashback_tva: number
  assurance_garantie: number
  utilisation_mois: number
  utilisation_par_index: number
  pouvoir_achat: number
  statut: string
  actif: boolean
}

export interface UserAdresse {
  id: number
  user_id: number
  libelle: string
  telephone: string
  adresse: string
  ville: string
  commune: string
  is_default: boolean
}

export interface CreateAdresseData {
  libelle: string
  ville: string
  commune: string
  adresse: string
  telephone: string
  user_id: number
  is_default?: boolean
}

export interface ProfilComplet {
  id: number
  nom: string
  prenom: string
  telephone: string
  email: string
  fonction: string
  salaire: number
  photo: string | null
  avatar: string | null
  age: number | null
  anciennete: string | null
  date_naissance: string | null
  date_engagement: string | null
  quotite_cessible_pourcent: number
  quotite_cessible_montant: number
  collectivite_id: number | null
  collectivite_nom: string | null
  souscription: Souscription | null
  prelevement_pourcent: number | null
  prelevement_montant: number | null
  pouvoir_achat: number | null
  revolving: number | null
  utilisation_total: number | null
  salaire_net: number | null
}

// Service Profil
const profilService = {

  // Récupérer le profil complet avec données financières
  async getProfilComplet(): Promise<ApiResponse<ProfilComplet>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/profil`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      const raw = data.data || data
      // Mapper les clés camelCase de l'API vers snake_case attendu par les composants
      const mapped: ProfilComplet = {
        id: raw.id,
        nom: raw.nom,
        prenom: raw.prenom,
        telephone: raw.telephone,
        email: raw.email,
        fonction: raw.fonction,
        salaire: raw.salaire ?? 0,
        photo: raw.photo ?? null,
        avatar: raw.avatar ?? null,
        age: raw.age ?? null,
        anciennete: raw.anciennete ?? null,
        date_naissance: raw.dateNaissance ?? raw.date_naissance ?? null,
        date_engagement: raw.dateEngagement ?? raw.date_engagement ?? null,
        quotite_cessible_pourcent: raw.quotiteCessiblePourcent ?? raw.quotite_cessible_pourcent ?? 0,
        quotite_cessible_montant: raw.quotiteCessibleMontant ?? raw.quotite_cessible_montant ?? 0,
        collectivite_id: raw.collectiviteId ?? raw.collectivite_id ?? null,
        collectivite_nom: raw.collectiviteNom ?? raw.collectivite_nom ?? null,
        souscription: raw.souscription ?? null,
        prelevement_pourcent: raw.prelevementPourcent ?? raw.prelevement_pourcent ?? null,
        prelevement_montant: raw.prelevementMontant ?? raw.prelevement_montant ?? null,
        pouvoir_achat: raw.pouvoirAchat ?? raw.pouvoir_achat ?? null,
        revolving: raw.revolving ?? null,
        utilisation_total: raw.utilisationTotal ?? raw.utilisation_total ?? null,
        salaire_net: raw.salaireNet ?? raw.salaire_net ?? null
      }
      return { status: response.status, message: 'Profil récupéré', data: mapped }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Récupérer la souscription active
  async getSouscription(): Promise<ApiResponse<Souscription>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/souscription`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      const raw = data.data || data
      // Mapper les clés camelCase de l'API vers snake_case attendu par les composants
      const mapped: Souscription = {
        id: raw.id,
        date_debut: raw.dateDebut ?? raw.date_debut,
        date_fin: raw.dateFin ?? raw.date_fin,
        duree_mois: raw.dureeMois ?? raw.duree_mois ?? 0,
        salaire: raw.salaire ?? 0,
        quotite_cessible_pourcent: raw.quotiteCessiblePourcent ?? raw.quotite_cessible_pourcent ?? 0,
        quotite_cessible_montant: raw.quotiteCessibleMontant ?? raw.quotite_cessible_montant ?? 0,
        pouvoir_achat: raw.pouvoirAchat ?? raw.pouvoir_achat ?? 0,
        pouvoir_achat_an: raw.pouvoirAchatAn ?? raw.pouvoir_achat_an ?? 0,
        revolving: raw.revolving ?? 0,
        prelevement_pourcent: raw.prelevementPourcent ?? raw.prelevement_pourcent ?? 0,
        prelevement_montant: raw.prelevementMontant ?? raw.prelevement_montant ?? 0,
        utilisation_total: raw.utilisationTotal ?? raw.utilisation_total ?? 0,
        salaire_net: raw.salaireNet ?? raw.salaire_net ?? 0,
        actif: raw.actif ?? false,
        details: (raw.details || []).map((d: Record<string, unknown>) => ({
          id: d.id,
          mois: d.mois ?? 0,
          date_prevision: d.datePrevision ?? d.date_prevision,
          date_operation: d.dateOperation ?? d.date_operation,
          decaissement: d.decaissement ?? 0,
          fond_risque: d.fondRisque ?? d.fond_risque ?? 0,
          commission: d.commission ?? 0,
          revolving: d.remboursementRevolving ?? d.revolving ?? 0,
          recharge: d.revolvingRecharge ?? d.recharge ?? 0,
          cashback_tva: d.cashbackTva ?? d.cashback_tva ?? 0,
          assurance_garantie: d.assuranceGarantie ?? d.assurance_garantie ?? 0,
          utilisation_mois: d.utilisationMois ?? d.utilisation_mois ?? 0,
          utilisation_par_index: d.utilisationParIndex ?? d.utilisation_par_index ?? 0,
          pouvoir_achat: d.pouvoirAchat ?? d.pouvoir_achat ?? 0,
          statut: d.statut ?? 'pending',
          actif: d.actif ?? false
        }))
      }
      return { status: response.status, message: 'Souscription récupérée', data: mapped }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Récupérer les adresses de livraison
  async getAdresses(): Promise<ApiResponse<UserAdresse[]>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/mobile/market/user_adresses`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: 'Adresses récupérées', data: data.data || data }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Créer une adresse de livraison
  async createAdresse(adresseData: CreateAdresseData): Promise<ApiResponse<UserAdresse>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/mobile/market/create_user_adresse`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(adresseData)
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: 'Adresse créée', data: data.data || data }
    } catch (error) {
      return handleApiError(error)
    }
  },

  // Calcul de souscription (preview sans sauvegarde)
  async calculSouscription(params?: Record<string, string>): Promise<ApiResponse> {
    try {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : ''
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/souscription/calcul${queryString}`, {
        method: 'GET',
        headers: getAuthHeaders()
      })
      const data = await response.json()
      if (!response.ok) {
        return handleApiError({ response: { status: response.status, data } })
      }
      return { status: response.status, message: 'Calcul effectué', data: data.data || data }
    } catch (error) {
      return handleApiError(error)
    }
  }
}

export default profilService
